import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { productService } from '@/services/productService';
import { Loader2, Plus, Minus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { OnChangeValue } from 'react-select';
import { useQueryClient } from '@tanstack/react-query';
import { useProductstableTranslations } from '@/hooks/useProductstableTranslations';
import { logError, logDebug } from '@/utils/errorHandler';

interface BulkTagModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: number[];
  onSuccess: () => void;
}

interface TagOption {
  label: string;
  value: string;
}

export function BulkTagModal({ open, onOpenChange, selectedIds, onSuccess }: BulkTagModalProps) {
  const [tagsToAdd, setTagsToAdd] = useState<TagOption[]>([]);
  const [tagsToRemove, setTagsToRemove] = useState<TagOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'add' | 'remove'>('add');
  const [allTags, setAllTags] = useState<TagOption[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const i18n = useProductstableTranslations();

  // Load all available tags when modal opens
  useEffect(() => {
    if (open) {
      loadAllTags();
    }
  }, [open]);

  // Load all tags for default options
  const loadAllTags = async () => {
    try {
      logDebug('Loading all tags...', { component: 'BulkTagModal', action: 'loadAllTags' });
      const tags = await productService.searchTags('');
      logDebug('Loaded tags', {
        component: 'BulkTagModal',
        action: 'loadAllTags',
        count: tags.length,
      });
      setAllTags(tags);
    } catch (error) {
      logError(
        'Error loading all tags',
        {
          component: 'BulkTagModal',
          action: 'loadAllTags',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
      setAllTags([]);
    }
  };

  // Load tag options with debounce
  const loadTagOptions = async (inputValue: string): Promise<TagOption[]> => {
    try {
      logDebug('Loading tag options for input', {
        component: 'BulkTagModal',
        action: 'loadTagOptions',
        inputValue,
      });

      // If no input, return all tags
      if (!inputValue.trim()) {
        return allTags;
      }

      // Search for tags matching the input
      const searchResults = await productService.searchTags(inputValue.trim());
      logDebug('Search results', {
        component: 'BulkTagModal',
        action: 'loadTagOptions',
        count: searchResults.length,
      });

      return searchResults;
    } catch (error) {
      logError(
        'Error loading tag options',
        {
          component: 'BulkTagModal',
          action: 'loadTagOptions',
          metadata: { inputValue, error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  };

  // Handle creating new tags
  const handleCreateTag = async (inputValue: string): Promise<TagOption | null> => {
    if (!inputValue.trim()) return null;

    try {
      logDebug('Creating new tag', {
        component: 'BulkTagModal',
        action: 'handleCreateTag',
        tagName: inputValue.trim(),
      });
      const newTag = await productService.createTag({ name: inputValue.trim() });
      logDebug('Created tag successfully', {
        component: 'BulkTagModal',
        action: 'handleCreateTag',
        tagId: newTag.id,
        tagName: newTag.name,
      });

      const newOption: TagOption = {
        label: newTag.name,
        value: newTag.name, // Use name as value for consistency with backend
      };

      // Add to all tags cache
      setAllTags(prev => [...prev, newOption]);

      toast({
        title: i18n.getBulkTagModalText('messages.tagCreated', {
          name: inputValue.trim(),
        }),
        variant: 'default',
      });

      return newOption;
    } catch (error) {
      logError(
        'Error creating tag',
        {
          component: 'BulkTagModal',
          action: 'handleCreateTag',
          metadata: { tagName: inputValue.trim(), error },
        },
        { showToast: false, userFacing: false }
      );
      toast({
        title: i18n.getBulkTagModalText('messages.failedToCreateTag'),
        variant: 'destructive',
      });
      return null;
    }
  };

  // Handle adding tags
  const handleAddTags = async () => {
    if (tagsToAdd.length === 0 || selectedIds.length === 0) return;

    setIsSubmitting(true);
    try {
      // Use the label (tag name) for the API call
      const tagNames = tagsToAdd.map(tag => tag.label);
      logDebug('Starting tag addition process', {
        component: 'BulkTagModal',
        action: 'handleAddTags',
        productCount: selectedIds.length,
        tagCount: tagNames.length,
        tagNames,
      });

      const result = await productService.bulkAddTags(selectedIds, tagNames);
      logDebug('Tag addition completed successfully', {
        component: 'BulkTagModal',
        action: 'handleAddTags',
        result,
      });

      toast({
        title: i18n.getBulkTagModalText('messages.tagsAddedToProducts', {
          count: selectedIds.length,
        }),
        description: i18n.getBulkTagModalText('messages.addedTags', {
          tags: tagNames.join(', '),
        }),
        variant: 'default',
      });

      // Reset and close
      setTagsToAdd([]);
      onOpenChange(false);
      onSuccess();

      // Invalidate all product queries to force refresh
      queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
      logDebug('Product queries invalidated, UI should refresh', {
        component: 'BulkTagModal',
        action: 'handleAddTags',
      });
    } catch (error) {
      logError(
        'Error adding tags to products',
        {
          component: 'BulkTagModal',
          action: 'handleAddTags',
          metadata: {
            productCount: selectedIds.length,
            tagNames: tagsToAdd.map(tag => tag.label),
            error,
          },
        },
        { showToast: false, userFacing: false }
      );
      toast({
        title: i18n.getBulkTagModalText('messages.failedToAddTags'),
        description: i18n.getBulkTagModalText('messages.errorAddingTags'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle removing tags
  const handleRemoveTags = async () => {
    if (tagsToRemove.length === 0 || selectedIds.length === 0) return;

    setIsSubmitting(true);
    try {
      // Use the label (tag name) for the API call
      const tagNames = tagsToRemove.map(tag => tag.label);
      logDebug('Starting tag removal process', {
        component: 'BulkTagModal',
        action: 'handleRemoveTags',
        productCount: selectedIds.length,
        tagCount: tagNames.length,
        tagNames,
      });

      const result = await productService.bulkRemoveTags(selectedIds, tagNames);
      logDebug('Tag removal completed successfully', {
        component: 'BulkTagModal',
        action: 'handleRemoveTags',
        result,
      });

      toast({
        title: i18n.getBulkTagModalText('messages.tagsRemovedFromProducts', {
          count: selectedIds.length,
        }),
        description: i18n.getBulkTagModalText('messages.removedTags', {
          tags: tagNames.join(', '),
        }),
        variant: 'default',
      });

      // Reset and close
      setTagsToRemove([]);
      onOpenChange(false);
      onSuccess();

      // Invalidate all product queries to force refresh
      queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
      logDebug('Product queries invalidated, UI should refresh', {
        component: 'BulkTagModal',
        action: 'handleRemoveTags',
      });
    } catch (error) {
      logError(
        'Error removing tags from products',
        {
          component: 'BulkTagModal',
          action: 'handleRemoveTags',
          metadata: {
            productCount: selectedIds.length,
            tagNames: tagsToRemove.map(tag => tag.label),
            error,
          },
        },
        { showToast: false, userFacing: false }
      );
      toast({
        title: i18n.getBulkTagModalText('messages.failedToRemoveTags'),
        description: i18n.getBulkTagModalText('messages.errorRemovingTags'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle tab change
  const handleTabChange = (value: string) => {
    setActiveTab(value as 'add' | 'remove');
  };

  // Reset state when modal closes
  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      setTagsToAdd([]);
      setTagsToRemove([]);
      setActiveTab('add');
    }
  };

  // Add CSS to ensure React Select works properly in dialogs
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'bulk-tag-modal-styles';
    style.textContent = `
      .react-select__menu {
        z-index: 60 !important;
      }
      .react-select__menu-portal {
        z-index: 60 !important;
      }
      [data-radix-dialog-content] .react-select__menu {
        z-index: 60 !important;
        position: relative !important;
      }
      [data-radix-dialog-content] .react-select__menu-list {
        max-height: 200px !important;
      }
    `;

    if (open) {
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById('bulk-tag-modal-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [open]);

  // Get current action based on active tab
  const getCurrentAction = () => {
    return activeTab === 'add' ? handleAddTags : handleRemoveTags;
  };

  // Get current tags based on active tab
  const getCurrentTags = () => {
    return activeTab === 'add' ? tagsToAdd : tagsToRemove;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {i18n.getBulkTagModalText('title', { count: selectedIds.length })}
          </DialogTitle>
          <DialogDescription>
            {i18n.getBulkTagModalText('description', {
              count: selectedIds.length,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="add" className="flex items-center gap-2">
                <Plus className="size-4" />
                {i18n.getBulkTagModalText('tabs.addTags')}
              </TabsTrigger>
              <TabsTrigger value="remove" className="flex items-center gap-2">
                <Minus className="size-4" />
                {i18n.getBulkTagModalText('tabs.removeTags')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="add" className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="add-tags">{i18n.getBulkTagModalText('labels.addTags')}</Label>
                <AsyncCreatableSelect<TagOption, true>
                  id="add-tags"
                  isMulti
                  cacheOptions
                  defaultOptions={allTags}
                  loadOptions={loadTagOptions}
                  onCreateOption={async inputValue => {
                    const newOption = await handleCreateTag(inputValue);
                    if (newOption) {
                      setTagsToAdd(prev => [...prev, newOption]);
                    }
                  }}
                  onChange={(newValue: OnChangeValue<TagOption, true>) => {
                    logDebug('Add tags onChange', {
                      component: 'BulkTagModal',
                      action: 'addTagsOnChange',
                      selectedCount: Array.isArray(newValue) ? newValue.length : 0,
                    });
                    setTagsToAdd(Array.isArray(newValue) ? newValue : []);
                  }}
                  value={tagsToAdd}
                  placeholder={i18n.getBulkTagModalText('placeholders.searchOrCreateTags')}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue
                      ? i18n.getBulkTagModalText('messages.noTagsFound', {
                          searchTerm: inputValue,
                        })
                      : i18n.getBulkTagModalText('messages.typeToSearchTags')
                  }
                  formatCreateLabel={(inputValue: string) =>
                    i18n.getBulkTagModalText('messages.formatCreateLabel', {
                      inputValue,
                    })
                  }
                  isDisabled={isSubmitting}
                  isClearable
                  closeMenuOnSelect={false}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: 40,
                      borderColor: state.isFocused ? '#3b82f6' : base.borderColor,
                      '&:hover': {
                        borderColor: '#3b82f6',
                      },
                    }),
                    menu: base => ({
                      ...base,
                      zIndex: 50,
                    }),
                    menuPortal: base => ({
                      ...base,
                      zIndex: 50,
                    }),
                  }}
                />
                {tagsToAdd.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    {i18n.getBulkTagModalText('messages.noTagsSelectedToAdd')}
                  </p>
                )}
                {tagsToAdd.length > 0 && (
                  <p className="text-sm text-blue-600">
                    {i18n.getBulkTagModalText('messages.selectedTagsToAdd', {
                      count: tagsToAdd.length,
                      tags: tagsToAdd.map(t => t.label).join(', '),
                    })}
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="remove" className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="remove-tags">{i18n.getBulkTagModalText('labels.removeTags')}</Label>
                <AsyncCreatableSelect<TagOption, true>
                  id="remove-tags"
                  isMulti
                  cacheOptions
                  defaultOptions={allTags}
                  loadOptions={loadTagOptions}
                  isValidNewOption={() => false} // Disable creation in remove mode
                  onChange={(newValue: OnChangeValue<TagOption, true>) => {
                    logDebug('Remove tags onChange', {
                      component: 'BulkTagModal',
                      action: 'removeTagsOnChange',
                      selectedCount: Array.isArray(newValue) ? newValue.length : 0,
                    });
                    setTagsToRemove(Array.isArray(newValue) ? newValue : []);
                  }}
                  value={tagsToRemove}
                  placeholder={i18n.getBulkTagModalText('placeholders.searchTagsToRemove')}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue
                      ? i18n.getBulkTagModalText('messages.noTagsFound', {
                          searchTerm: inputValue,
                        })
                      : i18n.getBulkTagModalText('messages.typeToSearchTags')
                  }
                  isDisabled={isSubmitting}
                  isClearable
                  closeMenuOnSelect={false}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: 40,
                      borderColor: state.isFocused ? '#dc2626' : base.borderColor,
                      '&:hover': {
                        borderColor: '#dc2626',
                      },
                    }),
                    menu: base => ({
                      ...base,
                      zIndex: 50,
                    }),
                    menuPortal: base => ({
                      ...base,
                      zIndex: 50,
                    }),
                  }}
                />
                {tagsToRemove.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    {i18n.getBulkTagModalText('messages.noTagsSelectedToRemove')}
                  </p>
                )}
                {tagsToRemove.length > 0 && (
                  <p className="text-sm text-red-600">
                    {i18n.getBulkTagModalText('messages.selectedTagsToRemove', {
                      count: tagsToRemove.length,
                      tags: tagsToRemove.map(t => t.label).join(', '),
                    })}
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            {i18n.getBulkTagModalText('buttons.cancel')}
          </Button>
          <Button
            onClick={getCurrentAction()}
            disabled={getCurrentTags().length === 0 || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                {activeTab === 'add'
                  ? i18n.getBulkTagModalText('buttons.adding')
                  : i18n.getBulkTagModalText('buttons.removing')}
              </>
            ) : (
              <>
                {activeTab === 'add' ? (
                  <Plus className="mr-2 size-4" />
                ) : (
                  <Minus className="mr-2 size-4" />
                )}
                {i18n.getBulkTagModalText('buttons.applyChanges')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
